// src/api/salonAuthService.js - Updated for multi-tenant salonId validation
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Default permissions per role
export const DEFAULT_PERMISSIONS = {
  owner: {
    canManageBranding: true,
    canManageUsers: true,
    canManageCustomers: true,
    canManageBookings: true,
    canManageReports: true
  },
  admin: {
    canManageBranding: true,
    canManageUsers: false,
    canManageCustomers: true,
    canManageBookings: true,
    canManageReports: true
  },
  manager: {
    canManageBranding: false,
    canManageUsers: false,
    canManageCustomers: true,
    canManageBookings: true,
    canManageReports: true
  },
  staff: {
    canManageBranding: false,
    canManageUsers: false,
    canManageCustomers: false,
    canManageBookings: false,
    canManageReports: false
  }
};

// Register a salon owner
export const registerSalonOwner = async (email, password, name, salonId) => {
  try {
    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Send email verification
    await sendEmailVerification(user);
    
    // Store additional user data in Firestore
    await setDoc(doc(db, 'salonUsers', user.uid), {
      uid: user.uid,
      email,
      name,
      salonId,
      role: 'owner',
      permissions: DEFAULT_PERMISSIONS.owner,
      createdAt: new Date().toISOString()
    });
    
    return user;
  } catch (error) {
    console.error('Error registering salon owner:', error);
    throw error;
  }
};

// Login for salon staff - UPDATED for multi-tenant salonId validation
export const loginSalonStaff = async (email, password, expectedSalonId = null) => {
  try {
    console.log('=== SALON STAFF LOGIN START ===');
    console.log('Email:', email);
    console.log('Expected SalonId:', expectedSalonId);
    
    // Sign in with Firebase Authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✓ Firebase authentication successful');
    console.log('User UID:', user.uid);
    
    // Get additional user data from Firestore salonUsers collection
    const userDoc = await getDoc(doc(db, 'salonUsers', user.uid));
    
    if (!userDoc.exists()) {
      console.error('✗ User not found in salonUsers collection');
      await signOut(auth); // Sign out the user
      throw new Error('User not found in database. Please contact administrator.');
    }
    
    const userData = userDoc.data();
    console.log('✓ User document found in salonUsers collection');
    console.log('User salonId:', userData.salonId);
    console.log('User role:', userData.role);
    
    // CRITICAL: Validate that user belongs to the expected salon
    if (expectedSalonId && userData.salonId !== expectedSalonId) {
      console.error('✗ SalonId mismatch!');
      console.error('Expected:', expectedSalonId);
      console.error('User belongs to:', userData.salonId);
      await signOut(auth); // Sign out the user
      throw new Error(`Access denied. This login is for ${expectedSalonId} staff only.`);
    }
    
    console.log('✓ SalonId validation passed');
    
    // Get salon info - check both clients and salons collections
    let salonName = 'Salon';
    let salonDoc = await getDoc(doc(db, 'clients', userData.salonId));
    
    if (salonDoc.exists()) {
      console.log('✓ Found salon in clients collection');
      const salonData = salonDoc.data();
      salonName = salonData.name || 'Salon';
    } else {
      console.log('Salon not found in clients, trying salons collection');
      salonDoc = await getDoc(doc(db, 'salons', userData.salonId));
      
      if (salonDoc.exists()) {
        console.log('✓ Found salon in salons collection');
        const salonData = salonDoc.data();
        salonName = salonData.branding?.name || salonData.name || 'Salon';
      } else {
        console.warn('⚠ Salon document not found in either collection');
      }
    }
    
    console.log('Salon name:', salonName);
    console.log('=== SALON STAFF LOGIN END (SUCCESS) ===');
    
    return {
      user: {
        uid: user.uid,
        email: user.email,
        ...userData,
        salonName: salonName
      }
    };
  } catch (error) {
    console.error('=== SALON STAFF LOGIN END (ERROR) ===');
    console.error('Error logging in:', error);
    throw error;
  }
};

// Create salon staff (manager, staff member, etc.) - SIGNS IN THE NEW USER
export const createStaffMember = async (email, password, name, role, permissions, salonId) => {
  try {
    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Send email verification
    await sendEmailVerification(user);
    
    // Store additional user data in Firestore
    await setDoc(doc(db, 'salonUsers', user.uid), {
      uid: user.uid,
      email,
      name,
      salonId,
      role,
      permissions,
      createdAt: new Date().toISOString()
    });
    
    return user;
  } catch (error) {
    console.error('Error creating staff member:', error);
    throw error;
  }
};

// NEW: Create staff member for SuperAdmin - PRESERVES CURRENT AUTH SESSION
export const createStaffMemberForSuperAdmin = async (email, password, name, role, permissions, salonId) => {
  try {
    console.log('Creating staff member for SuperAdmin without affecting current session');
    
    // Store the current user before creating new user
    const currentUser = auth.currentUser;
    const currentUserData = currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email,
      accessToken: currentUser.accessToken
    } : null;
    
    console.log('Current user before creation:', currentUserData);
    
    // Create user in Firebase Authentication (this will sign them in)
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    
    console.log('New user created:', newUser.uid);
    
    // Send email verification to the new user
    await sendEmailVerification(newUser);
    
    // Store additional user data in Firestore
    await setDoc(doc(db, 'salonUsers', newUser.uid), {
      uid: newUser.uid,
      email,
      name,
      salonId,
      role,
      permissions,
      createdAt: new Date().toISOString()
    });
    
    console.log('Firestore document created for new user');
    
    // Sign out the newly created user
    await signOut(auth);
    console.log('New user signed out');
    
    // Re-authenticate the original SuperAdmin if they existed
    if (currentUserData) {
      console.log('Attempting to restore SuperAdmin session...');
      
      // Wait a moment for the sign out to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Note: We can't automatically re-sign in the SuperAdmin here because
      // we don't have their password. The SuperAdmin context should handle
      // detecting this situation and prompting for re-authentication if needed.
      
      console.log('SuperAdmin will need to be re-authenticated by the context');
    }
    
    // Return the new user data (even though they're now signed out)
    return {
      uid: newUser.uid,
      email: newUser.email,
      emailVerified: newUser.emailVerified
    };
  } catch (error) {
    console.error('Error creating staff member for SuperAdmin:', error);
    
    // If there was an error, try to restore the original auth state
    if (auth.currentUser && auth.currentUser.email !== email) {
      console.log('Auth state appears to be preserved after error');
    }
    
    throw error;
  }
};

// Get current salon user
export const getCurrentSalonUser = async () => {
  if (!auth.currentUser) {
    return null;
  }

  try {
    const userDoc = await getDoc(doc(db, 'salonUsers', auth.currentUser.uid));
    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();

    // Merge role defaults with stored permissions so that any missing permission key
    // (e.g. canManageBookings absent on a legacy admin doc) inherits the role default
    // rather than evaluating to undefined → false. Explicitly stored values win.
    const roleDefaults = DEFAULT_PERMISSIONS[userData.role] || DEFAULT_PERMISSIONS.manager;
    const permissions = { ...roleDefaults, ...(userData.permissions || {}) };

    return {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      ...userData,
      permissions, // always present — either from Firestore or from role default
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

// Get all staff for a salon
export const getSalonStaff = async (salonId) => {
  try {
    const staffQuery = query(
      collection(db, 'salonUsers'),
      where('salonId', '==', salonId)
    );
    
    const staffSnapshot = await getDocs(staffQuery);
    const staffList = [];
    
    staffSnapshot.forEach((doc) => {
      staffList.push({
        uid: doc.id,
        ...doc.data()
      });
    });
    
    return staffList;
  } catch (error) {
    console.error('Error getting salon staff:', error);
    throw error;
  }
};

// Update staff member
export const updateStaffMember = async (uid, updatedData) => {
  try {
    await setDoc(doc(db, 'salonUsers', uid), updatedData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating staff member:', error);
    throw error;
  }
};

// Send password reset email
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw error;
  }
};

// Login for staff members using their email (no password)
export const loginStaffByUID = async (email, salonId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/staff-uid-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), salonId })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Login failed');
    }

    // Sign into Firebase Auth with the custom token
    await signInWithCustomToken(auth, data.customToken);

    return data.userData;
  } catch (error) {
    console.error('Error in staff UID login:', error);
    throw error;
  }
};

// Logout
export const logoutSalonUser = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// Check if user is authenticated and has staff role
export const isSalonUserAuthenticated = async () => {
  if (!auth.currentUser) {
    return false;
  }
  
  try {
    const userDoc = await getDoc(doc(db, 'salonUsers', auth.currentUser.uid));
    return userDoc.exists();
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};